import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 text-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[url('https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&q=60&w=1400')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative"><Logo /></div>
        <div className="relative max-w-md space-y-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold">Phase 1 · Invoice intelligence</p>
          <h2 className="font-display text-4xl font-bold leading-tight text-white">Scan a stack of supplier invoices. Get clean, structured purchasing data.</h2>
          <p className="text-slate-400">Auto-capture scanning, AI line-by-line extraction, supplier matching and price history — built for restaurant operators.</p>
        </div>
        <p className="relative text-xs text-slate-500">© {new Date().getFullYear()} Supplivo</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-8"><Logo light={false} /></div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

export function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full h-11" onClick={go} data-testid="google-login-button">
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </Button>
  );
}

export default function Login() {
  const { applySession } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      applySession(data);
      navigate(data?.user?.is_superadmin ? "/admin" : data?.user?.role === "staff" ? "/scan" : "/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to keep your purchasing data flowing.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" className="h-11" />
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center"><Label htmlFor="password">Password</Label><Link to="/forgot-password" className="text-xs text-emerald-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
          <Input id="password" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" className="h-11" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
        <div className="relative my-2 text-center text-xs text-slate-400"><span className="bg-slate-50 px-2 relative z-10">or</span><span className="absolute inset-x-0 top-1/2 h-px bg-slate-200" /></div>
        <GoogleButton />
      </form>
      <p className="text-sm text-slate-500 mt-6">Supplivo is invitation-only. New restaurant group? <Link to="/request-access" className="text-emerald-700 font-medium hover:underline" data-testid="request-access-link">Request access</Link></p>
    </AuthLayout>
  );
}
